// Global error safety net. Without an app.config.errorHandler, an uncaught
// error in any component render/handler propagates and blanks the whole SPA
// (the "หน้าขาว" white-screen). Installing a handler (a) keeps Vue alive after
// a component error instead of tearing down the app, and (b) surfaces the
// actual error text in a dismissible overlay so a white-screen becomes a
// readable, screenshot-able message rather than a blank page.
import type { App } from 'vue';

let overlayShown = false;

function showErrorOverlay(message: string): void {
  if (overlayShown || typeof document === 'undefined') return;
  overlayShown = true;

  const wrap = document.createElement('div');
  wrap.setAttribute('data-qjob-error-overlay', '');
  wrap.style.cssText =
    'position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;' +
    'padding:20px;background:rgba(15,23,42,.45);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);' +
    "font-family:'Sarabun','Inter',sans-serif;";
  wrap.innerHTML =
    '<div style="background:#fff;border-radius:20px;max-width:420px;width:100%;padding:26px;' +
    'box-shadow:0 24px 60px -20px rgba(15,23,42,.3);">' +
    '<div style="font-size:10px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:#94a3b8;margin-bottom:10px;">เกิดข้อผิดพลาด</div>' +
    '<h3 style="font-size:18px;font-weight:700;color:#0f172a;margin:0 0 8px;">แอปทำงานสะดุด</h3>' +
    '<p style="font-size:13px;color:#64748b;margin:0;line-height:1.6;">ระบบเจอข้อผิดพลาดตรงจุดนี้ กรุณาถ่ายภาพข้อความด้านล่างส่งให้ผู้ดูแล แล้วกด “โหลดใหม่”</p>' +
    '<pre data-msg style="font-size:11px;color:#b91c1c;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:8px;margin:12px 0 0;white-space:pre-wrap;word-break:break-word;max-height:170px;overflow:auto;"></pre>' +
    '<div style="display:flex;gap:10px;justify-content:flex-end;margin-top:18px;">' +
    '<button data-close style="font-size:13px;color:#94a3b8;background:none;border:0;padding:10px 6px;cursor:pointer;">ปิด</button>' +
    '<button data-reload style="font-size:13px;font-weight:500;color:#fff;background:#0f172a;border:0;border-radius:999px;padding:10px 20px;cursor:pointer;">โหลดใหม่</button>' +
    '</div></div>';

  const pre = wrap.querySelector('[data-msg]') as HTMLElement;
  pre.textContent = message.slice(0, 1500);
  wrap.querySelector('[data-reload]')!.addEventListener('click', () => location.reload());
  wrap.querySelector('[data-close]')!.addEventListener('click', () => {
    wrap.remove();
    overlayShown = false;
  });
  document.body.appendChild(wrap);
}

function formatError(err: unknown, info?: string): string {
  const base =
    err instanceof Error ? err.stack || err.message : typeof err === 'string' ? err : JSON.stringify(err);
  return info ? `[${info}]\n${base}` : String(base);
}

export function installGlobalErrorHandler(app: App): void {
  app.config.errorHandler = (err, _instance, info) => {
    // eslint-disable-next-line no-console
    console.error('[vue:errorHandler]', err, info);
    showErrorOverlay(formatError(err, info));
  };

  window.addEventListener('error', (e) => {
    // Resource-load errors (broken <img> etc.) have no .error — ignore those.
    if (e.error) showErrorOverlay(formatError(e.error));
  });
  window.addEventListener('unhandledrejection', (e) => {
    showErrorOverlay(formatError(e.reason));
  });
}
